/**
 * Popup Leaflet d'une soumission sur la carte des quêtes.
 *
 * Tout ce qui vient de la soumission (nom, email, libellés, valeurs, URL de
 * photo, identifiant) est saisi côté Spotter : il est échappé avant d'entrer
 * dans le HTML, sinon un Spotter pouvait exécuter du script dans la session
 * d'un admin (dont le jeton est lisible en sessionStorage).
 */

import { statutSoumission } from "./statutsQuete";

export interface SoumissionPopup {
  id?: string;
  statut?: string;
  createdAt?: string;
  reponsesJson?: string;
  photoUrl?: string;
  collecteurNom?: string;
  collecteurEmail?: string;
}

export function echapperHtml(valeur: unknown): string {
  return String(valeur ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/* Seuls http(s) et les images en data: sont acceptés (pas de javascript:). */
function urlPhotoSure(url?: string): string | null {
  if (!url) return null;
  return /^(https?:\/\/|data:image\/)/i.test(url.trim()) ? url.trim() : null;
}

/* L'identifiant finit dans un onclick : uniquement des caractères sûrs. */
function identifiantSur(id?: string): string | null {
  return id && /^[A-Za-z0-9_-]+$/.test(id) ? id : null;
}

export function buildPopupHtml(sub: SoumissionPopup): string {
  const st    = sub.statut ?? "en_attente";
  const color = st === "valide" ? "#356A0F" : st === "rejete" ? "#A23A0D" : st === "expire" ? "#57534A" : "#7C5600";
  const label = statutSoumission(st).libelle;
  const dateStr = sub.createdAt
    ? new Date(sub.createdAt).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";
  let champs = "";
  try {
    const reponses = JSON.parse(sub.reponsesJson || "[]");
    champs = reponses.map((r: any) => {
      let valeurStr = "";
      if (typeof r.valeur === "object" && r.valeur !== null && "lat" in r.valeur) {
        valeurStr = `GPS : ${Number(r.valeur.lat).toFixed(4)}, ${Number(r.valeur.lng).toFixed(4)}`;
      } else if (typeof r.valeur === "object" && r.valeur !== null) {
        valeurStr = JSON.stringify(r.valeur);
      } else {
        valeurStr = String(r.valeur ?? "");
      }
      return `<div style="font-size:11px;color:rgba(255,255,255,0.5);margin-top:4px;"><span style="color:rgba(255,255,255,0.3)">${echapperHtml(r.label)}</span> : ${echapperHtml(valeurStr)}</div>`;
    }).join("");
  } catch { /* skip */ }
  const photoUrl = urlPhotoSure(sub.photoUrl);
  const photo = photoUrl
    ? `<div style="margin:6px 0"><a href="${echapperHtml(photoUrl)}" target="_blank" rel="noopener noreferrer"><img src="${echapperHtml(photoUrl)}" style="width:120px;height:80px;object-fit:cover;border-radius:5px;border:1px solid rgba(255,255,255,0.15);display:block"/></a></div>`
    : "";
  const id = identifiantSur(sub.id);
  const actions = st === "en_attente" && id
    ? `<div style="display:flex;gap:6px;margin-top:8px">
        <button onclick="window.__tribeValidateSub('${id}')" style="flex:1;background:rgba(53,106,15,0.12);color:#356A0F;border:1px solid rgba(53,106,15,0.30);border-radius:5px;padding:5px 8px;cursor:pointer;font-size:11px;font-weight:700">✓ Valider</button>
        <button onclick="window.__tribeRejectSub('${id}')" style="flex:1;background:rgba(162,58,13,0.12);color:#A23A0D;border:1px solid rgba(162,58,13,0.30);border-radius:5px;padding:5px 8px;cursor:pointer;font-size:11px;font-weight:700">✗ Rejeter</button>
       </div>`
    : "";
  return `<div style="background:#0f0f1a;color:rgba(255,255,255,0.9);min-width:200px;font-family:system-ui,sans-serif;padding:2px 4px">
    <div style="background:${color}22;color:${color};border:1px solid ${color}55;border-radius:4px;display:inline-block;padding:2px 8px;font-size:10px;font-weight:700;margin-bottom:6px">${label}</div>
    <div style="font-size:12px;margin-bottom:2px"><span style="color:#8888aa">Spotter :</span> ${echapperHtml(sub.collecteurNom)} ${sub.collecteurEmail ? `(${echapperHtml(sub.collecteurEmail)})` : ""}</div>
    <div style="font-size:12px;margin-bottom:4px"><span style="color:#8888aa">Date :</span> ${echapperHtml(dateStr)}</div>
    ${photo}${champs}${actions}
  </div>`;
}
